import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import ErrorToast from "../ui/ErrorToast";
import { REPLY_LENGTH_OPTIONS } from "../../config/replyLengthOptions";
import { TONE_OPTIONS, normalizeToneValue } from "../../config/toneOptions";
import { leadsService } from "../../services/leads";
import { getApiErrorMessage } from "../../utils/apiError";
import { requestBodyScrollLock } from "../../utils/bodyScrollLock";
import { getScoreBadgeClass } from "../../utils/scoreColors";

function formatSentAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function companyRoleLine(companyRaw, roleRaw) {
  const company = (companyRaw || "").trim();
  const role = (roleRaw || "").trim();
  if (company && role) return `${company} | ${role}`;
  return company || role || "";
}

function ExpandableText({ text, className = "", collapsedLines = 2 }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const clampClass = collapsedLines === 3 ? "line-clamp-3" : "line-clamp-2";
  return (
    <p
      role={open ? undefined : "button"}
      tabIndex={open ? -1 : 0}
      onKeyDown={(e) => {
        if (!open && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          setOpen(true);
        }
      }}
      className={`text-sm text-theme-text leading-relaxed ${open ? "cursor-text" : `cursor-pointer ${clampClass}`} ${className}`}
      onClick={() => {
        if (!open) setOpen(true);
      }}
    >
      {text}
    </p>
  );
}

function DraftStatusInline({ regenerating }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-theme-dim">
      <span className="material-symbols-outlined text-lg animate-spin" style={{ fontSize: "18px" }} aria-hidden>
        progress_activity
      </span>
      {regenerating ? "Regenerating" : "Drafting"}
    </span>
  );
}

const LEAD_REPLY_GENERATION_STARTED = new Set();

const LeadDetailDrawer = ({ leadHash, onClose }) => {
  const queryClient = useQueryClient();
  const editWrapRef = useRef(null);

  const detailQuery = useQuery({
    queryKey: ["lead", leadHash],
    queryFn: () => leadsService.getLead(leadHash),
    enabled: Boolean(leadHash),
  });

  const lead = detailQuery.data;
  const [draftReply, setDraftReply] = useState("");
  const [apiError, setApiError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editTone, setEditTone] = useState("helpful_peer");
  const [editReplyLength, setEditReplyLength] = useState("medium");
  const [editCustomPrompt, setEditCustomPrompt] = useState("");
  const [originalOpen, setOriginalOpen] = useState(false);
  const originalSectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const stickyActionsRef = useRef(null);
  const editPopoverSnapshotRef = useRef(null);
  /** True while a Regenerate was explicitly requested; skips dirty-close duplicate mutate until cleared. */
  const explicitRegenerateRef = useRef(false);
  const prevEditOpenRef = useRef(false);

  useEffect(() => {
    if (lead?.reply_email?.body != null) {
      setDraftReply(lead.reply_email.body);
    }
  }, [leadHash, lead?.reply_email?.body]);

  useEffect(() => {
    setApiError("");
  }, [leadHash]);

  useEffect(() => {
    setEditOpen(false);
  }, [leadHash]);

  useEffect(() => {
    setOriginalOpen(false);
  }, [leadHash]);

  useEffect(() => {
    if (!originalOpen) return;
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      const section = originalSectionRef.current;
      if (!container || !section) return;
      const stickyHeight = stickyActionsRef.current?.getBoundingClientRect().height || 0;
      const containerTop = container.getBoundingClientRect().top;
      const sectionTop = section.getBoundingClientRect().top;
      const delta = sectionTop - containerTop - stickyHeight - 8;
      container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
    });
  }, [originalOpen]);

  useEffect(() => {
    if (!editOpen) return undefined;
    const onDown = (e) => {
      if (editWrapRef.current && !editWrapRef.current.contains(e.target)) {
        setEditOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [editOpen]);

  useEffect(() => {
    if (!leadHash) return undefined;
    const releaseScroll = requestBodyScrollLock();
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (editOpen) {
        setEditOpen(false);
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      releaseScroll();
      window.removeEventListener("keydown", onKey);
    };
  }, [leadHash, onClose, editOpen]);

  const composeReplyMutation = useMutation({
    mutationFn: (vars = {}) => leadsService.generateReply(leadHash, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadHash] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      setEditOpen(false);
    },
    onError: (e) => {
      explicitRegenerateRef.current = false;
      setApiError(getApiErrorMessage(e));
    },
  });

  useEffect(() => {
    const wasOpen = prevEditOpenRef.current;
    prevEditOpenRef.current = editOpen;

    if (!wasOpen || editOpen) {
      return undefined;
    }

    const snap = editPopoverSnapshotRef.current;
    editPopoverSnapshotRef.current = null;

    if (explicitRegenerateRef.current) {
      explicitRegenerateRef.current = false;
      return undefined;
    }

    if (!snap || !leadHash || composeReplyMutation.isPending) {
      return undefined;
    }

    if (snap.leadHash !== leadHash) {
      return undefined;
    }

    const hintNow = editCustomPrompt.trim();
    const dirty =
      editTone !== snap.tone ||
      editReplyLength !== snap.replyLength ||
      hintNow !== snap.promptNormalized;

    if (!dirty) {
      return undefined;
    }

    composeReplyMutation.mutate({
      tone: editTone,
      reply_length: editReplyLength,
      ...(hintNow ? { custom_instructions: hintNow } : {}),
    });

    return undefined;
    // Snapshot + edit fields captured when popover transitions closed; mutate is stable from useMutation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOpen]);

  useEffect(() => {
    if (!leadHash || detailQuery.isLoading || !lead) return;
    if (lead.reply_email != null) return;
    if (LEAD_REPLY_GENERATION_STARTED.has(leadHash)) return;
    LEAD_REPLY_GENERATION_STARTED.add(leadHash);
    composeReplyMutation.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot per mount session for this hash
  }, [leadHash, lead, detailQuery.isLoading]);

  const sendMutation = useMutation({
    mutationFn: () => leadsService.reviewAndSend(leadHash, draftReply),
    onError: (e) => {
      setApiError(getApiErrorMessage(e));
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadHash] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: () => leadsService.dismiss(leadHash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["history"] });
      onClose();
    },
  });

  const senderCompany = (lead?.sender_company || "").trim();
  const senderPerson = (lead?.sender_name || "").trim();
  const aboutCompanyName = senderCompany || senderPerson || "Company";
  const headerCompanyRole = lead ? companyRoleLine(lead.sender_company, lead.sender_role_title) : "";
  const headerTitle = detailQuery.isLoading
    ? "Loading…"
    : senderPerson || (lead?.sender_email || "").trim() || "Lead";

  const fromEmail = (lead?.sender_email || "").trim();
  const toMailbox = (lead?.mailbox_email || "").trim();
  const companyUrl = (lead?.company_website_url || "").trim();
  const canSendReply = Boolean((draftReply || "").trim());
  const inboxUrl = (lead?.inbox_url || "").trim();

  const serverDraftBodyTrimmed = ((lead?.reply_email?.body ?? "") || "").trim();
  const hasReplyRow = Boolean(lead?.reply_email);
  const textareaExpanded = serverDraftBodyTrimmed.length > 0;
  const composing = composeReplyMutation.isPending;

  const openEditPopover = () => {
    explicitRegenerateRef.current = false;
    const defTone = normalizeToneValue(
      (lead?.reply_email?.context?.reply_tone && String(lead.reply_email.context.reply_tone)) ||
        (lead?.brand_tone && String(lead.brand_tone)) ||
        "helpful_peer"
    );
    const defLen =
      (lead?.reply_email?.context?.reply_length && String(lead.reply_email.context.reply_length)) ||
      (lead?.brand_reply_length && String(lead.brand_reply_length)) ||
      "medium";
    const defPrompt = (lead?.reply_email?.context?.custom_prompt && String(lead.reply_email.context.custom_prompt)) || "";

    editPopoverSnapshotRef.current = {
      leadHash,
      tone: defTone,
      replyLength: defLen,
      promptNormalized: defPrompt.trim(),
    };

    setEditTone(defTone);
    setEditReplyLength(defLen);
    setEditCustomPrompt(defPrompt);
    setEditOpen(true);
  };

  const onRegenerate = () => {
    explicitRegenerateRef.current = true;
    setEditOpen(false);
    const hint = editCustomPrompt.trim();
    composeReplyMutation.mutate({
      tone: editTone,
      reply_length: editReplyLength,
      ...(hint ? { custom_instructions: hint } : {}),
    });
  };

  const drawer = (
    <div
      className="fixed inset-0 z-[100] flex min-h-[100vh] min-h-[100dvh] w-full max-w-none justify-end"
      aria-modal="true"
      role="dialog"
    >
      <style>{`
        @keyframes leadDrawerIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
      <button
        type="button"
        aria-label="Close lead detail"
        className="absolute inset-0 min-h-full w-full bg-black/40 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <aside
        className="relative z-10 flex h-full min-h-0 w-[min(36rem,100vw)] max-h-[100vh] max-h-[100dvh] flex-col border-l border-theme-border bg-theme-main shadow-2xl"
        style={{ animation: "leadDrawerIn 0.28s ease-out both" }}
      >
        <header className="border-b border-theme-border px-5 py-4 shrink-0 space-y-2">
          <div className="flex min-w-0 items-center gap-3">
            <h2 className="min-w-0 flex-1 truncate font-display font-bold text-xl leading-snug text-theme-on-background">
              {headerTitle}
            </h2>
            <div className="flex shrink-0 items-center gap-2">
              {lead?.match_score != null ? (
                <Badge variant="inherit" className={getScoreBadgeClass(lead.match_score)}>
                  {(lead?.brand_label || lead?.brand_name || "Brand").trim()} - {lead.match_score}%
                </Badge>
              ) : null}
              <button
                type="button"
                className="rounded-lg border border-theme-border px-3 py-1.5 font-mono text-[11px] text-theme-dim hover:border-theme-border-bright hover:text-theme-text"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
          {headerCompanyRole ? (
            <p className="font-mono text-[11px] leading-relaxed text-theme-dim break-words">
              {headerCompanyRole}
            </p>
          ) : null}
          {fromEmail || toMailbox ? (
            <p className="font-mono text-[11px] leading-relaxed text-theme-dim break-words">
              {fromEmail ? <span>{fromEmail}</span> : null}
              {fromEmail && toMailbox ? (
                <span className="mx-1 inline align-middle" aria-hidden>
                  <span
                    className="material-symbols-outlined inline-block align-middle text-theme-dim"
                    style={{ fontSize: "12px", lineHeight: 1, verticalAlign: "middle" }}
                  >
                    arrow_forward
                  </span>
                </span>
              ) : null}
              {toMailbox ? <span>{toMailbox}</span> : null}
            </p>
          ) : null}
        </header>

        <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5 flipmail-drawer-scroll">
          {detailQuery.isError ? (
            <p className="text-sm text-theme-dim">Could not load this lead.</p>
          ) : null}

          {lead ? (
            <>
              <section className="space-y-2 pb-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-text opacity-75">Proposed reply</h3>
                  <div className="flex min-h-[26px] items-center justify-end gap-3 shrink-0">
                    {composing ? <DraftStatusInline regenerating={hasReplyRow} /> : null}
                    <button
                      type="button"
                      className="font-mono text-[10px] uppercase tracking-[0.08em] text-theme-dim opacity-50 cursor-not-allowed"
                      disabled
                    >
                      Save draft
                    </button>
                  </div>
                </div>
                <ErrorToast message={apiError} onDismiss={() => setApiError("")} />
                <textarea
                  className={`w-full rounded-lg bg-theme-main border border-theme-border text-theme-text font-mono text-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-theme-accent/60 focus:border-theme-accent ease-out ${
                    textareaExpanded ? "min-h-[13rem]" : "min-h-[6.5rem]"
                  } transition-[min-height] duration-500`}
                  placeholder="Draft will appear here after generation, or write your own."
                  value={draftReply}
                  onChange={(e) => setDraftReply(e.target.value)}
                />
              </section>

              <section ref={stickyActionsRef} className="sticky top-[-1rem] z-20 -mx-5 !mt-0 border-y border-theme-border bg-theme-main/75 px-5 py-3 backdrop-blur-md shadow-[0_10px_20px_rgba(0,0,0,0.14)]">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {hasReplyRow ? (
                      <div className="relative" ref={editWrapRef}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 px-3 inline-flex items-center gap-1"
                          onClick={() => (editOpen ? setEditOpen(false) : openEditPopover())}
                        >
                          <span className="material-symbols-outlined text-[16px] leading-none" aria-hidden>
                            edit_square
                          </span>
                          Edit
                        </Button>
                        {editOpen ? (
                          <div
                            className="absolute bottom-full left-0 z-40 mb-1.5 flex max-h-[min(72vh,24rem)] w-[min(22rem,calc(100vw-2rem))] flex-col rounded-lg border border-theme-border bg-theme-secondary p-2.5 shadow-xl"
                            role="dialog"
                            aria-label="Regenerate draft"
                          >
                            <div className="flipmail-scrollbar-hover min-h-0 space-y-2 overflow-y-auto pr-0.5">
                              <div className="grid grid-cols-2 gap-2">
                                <div className="min-w-0">
                                  <p className="mb-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-theme-text opacity-75">
                                    Tone
                                  </p>
                                  <div className="flex flex-col gap-0.5">
                                    {TONE_OPTIONS.map((item) => (
                                      <label
                                        key={item.value}
                                        title={item.subtitle}
                                        className="flex cursor-pointer items-center gap-1.5 rounded border border-theme-border px-1.5 py-1 text-left hover:bg-theme-main/80"
                                      >
                                        <input
                                          type="radio"
                                          name="edit-tone"
                                          className="shrink-0"
                                          checked={editTone === item.value}
                                          onChange={() => setEditTone(item.value)}
                                        />
                                        <span className="min-w-0 truncate font-mono text-[11px] leading-snug text-theme-text">
                                          {item.title}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <div className="min-w-0">
                                  <p className="mb-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-theme-text opacity-75">
                                    Length
                                  </p>
                                  <div className="flex flex-col gap-0.5">
                                    {REPLY_LENGTH_OPTIONS.map((item) => (
                                      <label
                                        key={item.value}
                                        title={item.subtitle}
                                        className="flex cursor-pointer items-center gap-1.5 rounded border border-theme-border px-1.5 py-1 text-left hover:bg-theme-main/80"
                                      >
                                        <input
                                          type="radio"
                                          name="edit-reply-length"
                                          className="shrink-0"
                                          checked={editReplyLength === item.value}
                                          onChange={() => setEditReplyLength(item.value)}
                                        />
                                        <span className="min-w-0 truncate font-mono text-[11px] leading-snug text-theme-text">
                                          {item.title}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div className="shrink-0">
                                <label
                                  htmlFor="reply-custom-hint"
                                  className="mb-0.5 block font-mono text-[9px] uppercase tracking-[0.1em] text-theme-text opacity-75"
                                >
                                  Extra <span className="normal-case opacity-70">(optional)</span>
                                </label>
                                <textarea
                                  id="reply-custom-hint"
                                  rows={2}
                                  className="w-full rounded-md border border-theme-border bg-theme-main px-2 py-1 font-mono text-[11px] text-theme-text focus:outline-none focus:ring-1 focus:ring-theme-accent/60"
                                  placeholder="e.g. Mention pilot program"
                                  value={editCustomPrompt}
                                  onChange={(e) => setEditCustomPrompt(e.target.value)}
                                />
                              </div>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              className="mt-2 w-full shrink-0"
                              loading={composing}
                              disabled={composing}
                              onClick={() => onRegenerate()}
                            >
                              Regenerate
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-9 px-3 inline-flex items-center gap-1"
                      onClick={() => dismissMutation.mutate()}
                      disabled={dismissMutation.isPending}
                    >
                      <span className="material-symbols-outlined text-[16px] leading-none">thumb_down</span>
                      Dismiss
                    </Button>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="h-9 px-3 inline-flex items-center gap-1"
                    disabled={!canSendReply || sendMutation.isPending}
                    onClick={() => sendMutation.mutate()}
                  >
                    Send reply
                    <span className="material-symbols-outlined text-[16px] leading-none">chevron_right</span>
                  </Button>
                </div>
              </section>

              {lead.match_reason ? (
                <section className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <h3 className="min-w-0 flex-1 font-mono text-[10px] uppercase tracking-[0.1em] text-theme-text opacity-75">
                      {(lead.match_score != null ? Number(lead.match_score) : 0)}% Match for{" "}
                      {(lead?.brand_label || lead?.brand_name || "Brand").trim()}
                    </h3>
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                      {lead.company_match_score != null ? (
                        <Badge variant="inherit" className={getScoreBadgeClass(lead.company_match_score)}>
                          {lead.company_match_score}% Company Match
                        </Badge>
                      ) : null}
                      {lead.sender_role_title?.trim() && lead.role_match_score != null ? (
                        <Badge variant="inherit" className={getScoreBadgeClass(lead.role_match_score)}>
                          {lead.role_match_score}% Role Match
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  <ExpandableText text={lead.match_reason} collapsedLines={3} />
                </section>
              ) : null}

              {lead.sell_summary ? (
                <section className="space-y-2">
                  <h3 className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-text opacity-75">
                    About{" "}
                    {companyUrl ? (
                      <a
                        href={companyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:opacity-90"
                      >
                        {aboutCompanyName}
                        <span
                          className="material-symbols-outlined inline-block shrink-0 leading-none align-middle"
                          style={{ fontSize: "10px", lineHeight: 1 }}
                          aria-hidden
                        >
                          open_in_new
                        </span>
                      </a>
                    ) : (
                      <span>{aboutCompanyName}</span>
                    )}
                  </h3>
                  <ExpandableText text={lead.sell_summary} collapsedLines={3} />
                </section>
              ) : null}

              {lead.cold_pitch_summary ? (
                <section className="space-y-2">
                  <h3 className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-text opacity-75">Their Pitch</h3>
                  <ExpandableText text={lead.cold_pitch_summary} />
                </section>
              ) : null}

              <section ref={originalSectionRef} className="space-y-2">
                <h3 className="font-mono text-[10px] text-theme-text opacity-75 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="uppercase tracking-[0.1em]">Original message</span>
                  {inboxUrl ? (
                    <a
                      href={inboxUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:opacity-90 font-normal tracking-normal normal-case text-[11px]"
                    >
                      View in Gmail
                      <span
                        className="material-symbols-outlined inline-block shrink-0 leading-none align-middle"
                        style={{ fontSize: "10px", lineHeight: 1 }}
                        aria-hidden
                      >
                        open_in_new
                      </span>
                    </a>
                  ) : null}
                </h3>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (!originalOpen) setOriginalOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (!originalOpen && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      setOriginalOpen(true);
                    }
                  }}
                  className={`flipmail-scrollbar-hover rounded-lg border border-theme-border bg-theme-main/80 p-3 transition-[max-height] duration-300 ease-out ${
                    originalOpen ? "max-h-[9999px] overflow-visible cursor-text" : "max-h-40 overflow-hidden cursor-pointer"
                  }`}
                >
                  {(lead.subject || lead.received_at) ? (
                    <div className="mb-2 flex items-center justify-between gap-3 border-b border-theme-border pb-2">
                      <p className="min-w-0 truncate text-[12px] font-semibold text-theme-text opacity-80">
                        {lead.subject || "No subject"}
                      </p>
                      {lead.received_at ? (
                        <p className="shrink-0 font-mono text-[11px] text-theme-dim">{formatSentAt(lead.received_at)}</p>
                      ) : null}
                    </div>
                  ) : null}
                  <div>
                    <p
                      className={`text-sm text-theme-text break-words leading-relaxed ${
                        originalOpen ? "whitespace-pre-wrap" : "whitespace-normal line-clamp-6"
                      }`}
                    >
                      {lead.original_email || "No thread text yet."}
                    </p>
                    {!originalOpen ? <p className="mt-1 text-theme-dim">...</p> : null}
                  </div>
                </div>
              </section>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );

  if (typeof document === "undefined") {
    return null;
  }
  return createPortal(drawer, document.body);
};

export default LeadDetailDrawer;
